import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { BrandingResponse } from "subModule/src/services/allcommon/interface";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import { useSnackbar } from "notistack";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import MonthlyPurchaseOrders from "components/InventoryAnalytics/MonthlyPurchaseOrders";
import MostConsumedParts from "components/InventoryAnalytics/MostConsumedParts";
import MonthlyConsumptionTrend from "components/InventoryAnalytics/MonthlyConsumptionTrend";
import OperationalKPIs from "components/InventoryAnalytics/OperationalKPIs";
import InventoryHealthKPIs from "components/InventoryAnalytics/InventoryHealthKPIs";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import DashboardPageTitle from "components/Analytics/DashboardPageTitle";

export default function InventoryAnalytics() {
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const branding = useSelector(brandingInfo);
  const [storesLoaded, setStoresLoaded] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [globalFilterApplied, setGlobalFilterApplied] =
    useState<boolean>(false);
  const durationOptions = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const { control, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      globalFilters: {
        range: { value: "monthly", label: t("mtd") },
        allStore: false,
      },
    },
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const firstRender = useRef(true);
  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const themeColors =
    (branding as BrandingResponse)?.attributes?.themeId === 100
      ? (branding as BrandingResponse)?.attributes?.customTheme
      : (themes as any)[
          (branding as BrandingResponse)?.attributes?.themeId || 3
        ];
  const allStore = 0;
  const stores = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  if ((storesData?.pages[0]?.data?.content?.length ?? 0) > 1)
    stores.splice(0, 0, { value: allStore, label: t("allStores") });
  useEffect(() => {
    const getStoresData = setTimeout(async () => {
      await storesRefetch();
      setStoresLoaded(true);
    }, 50);
    return () => clearTimeout(getStoresData);
  }, []);
  useEffect(() => {
    if (firstRender.current && storesLoaded && stores.length > 0) {
      firstRender.current = false;
      const defaultStore = (stores.length > 1 ? stores[1] : stores[0]) || null;
      setValue("globalFilters.storeId", defaultStore);
    }
  }, [storesLoaded, stores]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => storesRefetch().finally(() => setSearchLoading(null)),
      300,
    );
  };
  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        className="mb-[-8px] sticky top-[43px] z-10"
        style={{ background: themeColors?.appBg || "#F1F6F9" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} lg={5}>
            <DashboardPageTitle
              title={t("inventoryAnalytics")}
              color={themeColors?.primary}
            />
          </Grid>
          <Grid item xs={12} md={8} lg={7}>
            <Grid container spacing={2} className="justify-end pt-2">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.storeId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={stores}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        if (selectedOption?.value === 0)
                          setValue("globalFilters.allStore", true);
                        else setValue("globalFilters.allStore", false);
                        setGlobalFilterApplied(true);
                      }}
                      isLoading={searchLoading === "globalFilters"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "globalFilters",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions(
                              "globalFilters",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(storesData?.pages[0]?.data as any)?.last)
                          handleDropdownActions("globalFilters", "loadNext");
                      }}
                      page={dropdownPage}
                      defaultPlaceholder={t("store")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.range"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={durationOptions}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setGlobalFilterApplied(true);
                      }}
                      defaultPlaceholder={t("duration")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        {checkPrivileges("ROLE_INVENTORY_DASHBOARD_PO_VIEW") ? (
          <MonthlyPurchaseOrders
            themes={themeColors}
            stores={stores}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              storesLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              dropdownPage,
              searchLoading,
              setDropdownPage,
              storesData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className="flex gap-1 justify-between mb-5">
                  <Typography
                    variant="h1"
                    gutterBottom
                    className="font-Saira text-[13px] font-semibold text-start mb-0 mt-[5px]"
                    sx={{ color: themeColors?.primary }}
                  >
                    {t("purchaseOrdersThisMonth")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {checkPrivileges("ROLE_INVENTORY_DASHBOARD_PARTS_VIEW") ? (
          <MostConsumedParts
            themes={themeColors}
            stores={stores}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              storesLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              dropdownPage,
              searchLoading,
              setDropdownPage,
              storesData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className="flex gap-1 justify-between mb-5">
                  <Typography
                    variant="h1"
                    gutterBottom
                    className="font-Saira text-[13px] font-semibold text-start mb-0 mt-[5px]"
                    sx={{ color: themeColors?.primary }}
                  >
                    {t("mostConsumedSpareParts")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {checkPrivileges("ROLE_INVENTORY_DASHBOARD_CONSUMPTION_VIEW") ? (
          <MonthlyConsumptionTrend
            themes={themeColors}
            stores={stores}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              storesLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              dropdownPage,
              searchLoading,
              setDropdownPage,
              storesData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className="flex gap-1 justify-between mb-5">
                  <Typography
                    variant="h1"
                    gutterBottom
                    className="font-Saira text-[13px] font-semibold text-start mb-0 mt-[5px]"
                    sx={{ color: themeColors?.primary }}
                  >
                    {t("monthlyConsumptionTrend")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {checkPrivileges("ROLE_INVENTORY_DASHBOARD_KPI_VIEW") ? (
          <OperationalKPIs
            themes={themeColors}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "285px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className="flex gap-1 justify-between mb-5">
                  <Typography
                    variant="h1"
                    gutterBottom
                    className="font-Saira text-[13px] font-semibold text-start mb-0 mt-[5px]"
                    sx={{ color: themeColors?.primary }}
                  >
                    {t("operationalKPIs")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
      <Grid item xs={12}>
        {checkPrivileges("ROLE_INVENTORY_DASHBOARD_HEALTH_VIEW") ? (
          <InventoryHealthKPIs
            themes={themeColors}
            stores={stores}
            handleDropdownActions={handleDropdownActions}
            globalFilters={watch("globalFilters")}
            {...{
              enqueueSnackbar,
              storesLoaded,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              dropdownPage,
              searchLoading,
              setDropdownPage,
              storesData,
              globalFilterApplied,
              setGlobalFilterApplied,
            }}
          />
        ) : (
          <Tooltip title={t("permissionRequired")} followCursor>
            <Card
              sx={{
                opacity: 0.6,
                height: "310px",
                boxShadow: "none",
                borderRadius: "8px",
                "& .MuiCardContent-root": { padding: "16px !important" },
              }}
            >
              <CardContent>
                <Box className="flex gap-1 justify-between mb-5">
                  <Typography
                    variant="h1"
                    gutterBottom
                    className="font-Saira text-[13px] font-semibold text-start mb-0 mt-[5px]"
                    sx={{ color: themeColors?.primary }}
                  >
                    {t("inventoryHealthKPIs")}
                  </Typography>
                </Box>
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
                <Skeleton
                  animation={false}
                  sx={{ backgroundColor: themeColors?.secondary }}
                />
              </CardContent>
            </Card>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}
